/**
 * App-level owner of the GPS location stream and track recording.
 *
 * WHY THIS EXISTS: location tracking + recording used to be owned by the
 * `cairn-map` component, so navigating away from the map (component unmount)
 * called `dispose()` → `stopLocationTracking()` and killed the foreground
 * service mid-recording — silently dropping the rest of the track until the
 * user returned to a map. (Diagnosed from a Longs Peak trip: one 65-min /
 * 1.4 km straight-line gap that exactly matched a navigate-away window.)
 *
 * The fix: own the foreground-service lifecycle, the single location-stream
 * subscription, and the recording manager here, at module scope. Map views are
 * pure subscribers — they bind a `view` for marker/UI rendering and register as
 * a "consumer" while mounted, but their lifecycle no longer controls GPS.
 *
 * GPS runs while `recording || (consumers > 0 && visible)`; it stops (device
 * sleeps) when neither. No wake lock is needed: trip data showed an *alive*
 * foreground service captures continuously across multi-hour screen-off
 * stretches.
 */
import { compassService } from '../compass/compass-provider'
import {
  LocationTrackingManager,
  type LocationTrackingCallbacks,
} from '../../components/map/managers/location-tracking-manager.js'
import {
  TrackRecordingManager,
  type TrackRecordingCallbacks,
} from '../../components/map/managers/track-recording-manager.js'
import { LocationPersistenceService, type StoredLocation } from './location-persistence.js'
import { SettingsStorage } from '../../storage/settings-storage.js'
import { router } from '../../utils/router.js'
import { logger } from '../../utils/logger.js'
import type maplibregl from 'maplibre-gl'

const log = logger.create('location-svc')

/**
 * The UI surface a mounted map binds via `attachView`. Every method is safe to
 * skip when no map is mounted — recording keeps running; these just no-op.
 * Mirrors the view-facing subset of the two managers' callbacks.
 */
export interface LocationServiceView {
  onLocationUpdated: LocationTrackingCallbacks['onLocationUpdated']
  onElevationUpdated: LocationTrackingCallbacks['onElevationUpdated']
  onVelocityUpdated: LocationTrackingCallbacks['onVelocityUpdated']
  onHasLocationData: LocationTrackingCallbacks['onHasLocationData']
  onHeadingUpdated: LocationTrackingCallbacks['onHeadingUpdated']
  getMapZoom: LocationTrackingCallbacks['getMapZoom']
  onRecordingChanged: TrackRecordingCallbacks['onRecordingChanged']
  showNotification: TrackRecordingCallbacks['showNotification']
  onFeaturesCreated: NonNullable<TrackRecordingCallbacks['onFeaturesCreated']>
  onProfileUpdated: NonNullable<TrackRecordingCallbacks['onProfileUpdated']>
}

class LocationRecordingService {
  private view: LocationServiceView | undefined
  private consumers = 0
  private visible = true

  /** View-level renderer (marker, accuracy circle, compass arrow, follow). */
  readonly location: LocationTrackingManager
  /** App-level recording state machine. */
  readonly recording: TrackRecordingManager

  constructor() {
    const locationCallbacks: LocationTrackingCallbacks = {
      onLocationUpdated: loc => this.view?.onLocationUpdated(loc),
      onElevationUpdated: e => this.view?.onElevationUpdated(e),
      onVelocityUpdated: v => this.view?.onVelocityUpdated(v),
      onHasLocationData: () => this.view?.onHasLocationData(),
      onHeadingUpdated: h => this.view?.onHeadingUpdated(h),
      // App-level recording pipe — runs whether or not a map is mounted, so
      // navigating away from the map no longer truncates the track.
      onTrackPoint: loc => {
        if (this.recording.isRecording) this.recording.addTrackPoint(loc)
      },
      storeLocation: data => LocationPersistenceService.storeLocation(data as StoredLocation),
      getMapZoom: () => this.view?.getMapZoom(),
      getTrackQuality: () =>
        new SettingsStorage().getSettingsWithDefaults().tracking?.trackQuality ?? 'standard',
      isRecording: () => this.recording.isRecording,
    }
    this.location = new LocationTrackingManager(locationCallbacks, compassService)

    const recordingCallbacks: TrackRecordingCallbacks = {
      onRecordingChanged: isRecording => {
        this.view?.onRecordingChanged(isRecording)
        // Recording demand changed → may need to start/stop the service.
        this.reconcile()
      },
      showNotification: (type, title, message, activityId) =>
        this.view?.showNotification(type, title, message, activityId),
      // Router is app-level; works even if no map view is attached.
      navigateToActivity: id => void router.navigate(`/activities/${id}`),
      onFeaturesCreated: features => this.view?.onFeaturesCreated(features),
      getCurrentLocation: () => this.location.getCurrentLocation(),
      onProfileUpdated: data => this.view?.onProfileUpdated(data),
    }
    this.recording = new TrackRecordingManager(recordingCallbacks, compassService)

    // App-level screen-state handling. Owned here (not by the map component) so
    // the buffer drain + request-param updates run even when no map is mounted
    // — e.g. recording continues after navigating away from the map.
    if (typeof document !== 'undefined') {
      this.visible = document.visibilityState === 'visible'
      document.addEventListener('visibilitychange', () => {
        void this.onScreenStateChanged(document.visibilityState === 'visible')
      })
    }
  }

  get isRecording(): boolean {
    return this.recording.isRecording
  }

  // --- View binding (a mounted map) ---

  attachView(view: LocationServiceView): void {
    this.view = view
    // Sync current recording state into the freshly-mounted UI.
    view.onRecordingChanged(this.recording.isRecording)
  }

  detachView(view: LocationServiceView): void {
    if (this.view === view) this.view = undefined
  }

  /** Bind the live map to both managers (marker + recording track line). */
  setMap(map: maplibregl.Map): void {
    this.location.setMap(map)
    this.recording.setMap(map)
  }

  /** The map is going away (component unmount). Drop renderer state; keep GPS. */
  detachMap(): void {
    this.location.detachMap()
    this.recording.detachMap()
  }

  // --- Demand-gated GPS lifecycle ---

  /** A map view became active and wants the live marker. */
  addConsumer(): void {
    this.consumers++
    this.reconcile()
  }

  /** A map view went away. */
  removeConsumer(): void {
    this.consumers = Math.max(0, this.consumers - 1)
    this.reconcile()
  }

  /** Screen visibility changed (app foreground/background). */
  async onScreenStateChanged(isVisible: boolean): Promise<void> {
    this.visible = isVisible
    // Start/stop based on the new demand, THEN let the manager update request
    // params + drain the native buffer (it assumes the service is running).
    this.reconcile()
    await this.location.onScreenStateChanged(isVisible)
  }

  /**
   * Start GPS iff something needs it; stop it (sleep) when nothing does.
   *
   * CRITICAL: Android (API 31+) forbids *starting* a foreground service from
   * the background. So we only ever START when the app is foreground
   * (`visible`). While recording, the service must already be running (it is
   * started, awaited, from the foreground record gesture in `toggleRecording`),
   * so on backgrounding we leave it running and never attempt a (doomed)
   * background restart. Stopping is always allowed.
   */
  private reconcile(): void {
    if (this.recording.isRecording) {
      if (this.visible) {
        compassService.logDiagnostic('LOCSVC', 'reconcile: recording + visible → ensure started')
        void this.location.startLocationTracking()
      } else {
        compassService.logDiagnostic(
          'LOCSVC',
          'reconcile: recording + background → leave running (no bg start)'
        )
      }
      return // never stop while recording
    }
    if (this.consumers > 0 && this.visible) {
      compassService.logDiagnostic('LOCSVC', `reconcile: ${this.consumers} consumer(s) → start`)
      void this.location.startLocationTracking()
    } else {
      compassService.logDiagnostic(
        'LOCSVC',
        `reconcile: idle (consumers=${this.consumers} visible=${this.visible}) → stop`
      )
      this.location.stopLocationTracking()
    }
  }

  // --- Passthroughs the map view uses ---

  requestImmediateLocation(): Promise<void> {
    return this.location.requestImmediateLocation()
  }

  startCompassTracking(): Promise<void> {
    return this.location.startCompassTracking()
  }

  followToCurrentLocation(): void {
    this.location.followToCurrentLocation()
  }

  panToCurrentLocation(): void {
    this.location.panToCurrentLocation()
  }

  // --- Recording controls ---

  /**
   * Toggle recording. On stop, force a final native-buffer drain first so any
   * fixes captured while off a map view (or via a quick-settings stop) are
   * merged into the track before it is saved.
   */
  async toggleRecording(): Promise<void> {
    if (this.recording.isRecording) {
      try {
        await compassService.flushBufferedLocations()
      } catch (e) {
        log.warn('Final buffer drain on stop failed', e)
      }
    } else {
      // Starting: bring the foreground service up NOW, from this guaranteed-
      // foreground user gesture, and await it — so it is already running before
      // the app is ever backgrounded (a background start would be denied on
      // API 31+). Don't rely on reconcile() alone for the start.
      this.visible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true
      compassService.logDiagnostic('LOCSVC', 'toggleRecording: start → foreground service start')
      try {
        await this.location.startLocationTracking()
      } catch (e) {
        log.warn('Foreground service start on record failed', e)
      }
    }
    await this.recording.toggle()
  }

  tryResumeRecording(): Promise<void> {
    return this.recording.tryResumeRecording()
  }
}

export const locationService = new LocationRecordingService()
